import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import EpisodeCard from "@/components/EpisodeCard";
import { show, episodes } from "@/lib/podcast";

const PER_PAGE = 12;

export default function Episodes() {
  const [q, setQ] = useState("");
  // Keep the current page in the URL (?page=N) so browser Back restores the
  // page the visitor was on instead of resetting to page 1.
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return episodes;
    return episodes.filter(
      (e) =>
        e.title.toLowerCase().includes(t) ||
        e.excerpt.toLowerCase().includes(t),
    );
  }, [q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));

  // Reset to page 1 whenever the search changes.
  const onSearch = (e) => {
    setQ(e.target.value);
    if (searchParams.get("page")) {
      const next = new URLSearchParams(searchParams);
      next.delete("page");
      setSearchParams(next, { replace: true });
    }
  };

  const current = Math.min(page, totalPages);
  const start = (current - 1) * PER_PAGE;
  const pageItems = filtered.slice(start, start + PER_PAGE);

  const goTo = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="episodes-page">
      <Seo
        title="Episodes"
        description={`Browse every episode of ${show.title} — ${show.description}`}
        image={show.image}
        path="/episodes"
      />

      <section className="hero-bg border-b border-[#0b1f4d]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9a6700]">
            The Archive
          </span>
          <h1 className="font-poster uppercase text-5xl sm:text-6xl text-[#0b1f4d] mt-2 tracking-tight">
            Episodes
          </h1>
          <p className="mt-4 text-lg text-[#374566] max-w-2xl">
            Every episode, newest first. New episodes are added automatically
            from the feed.
          </p>

          <div className="mt-8 relative max-w-md">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#66718d]"
            />
            <input
              data-testid="episode-search-input"
              value={q}
              onChange={onSearch}
              placeholder="Search episodes…"
              className="w-full rounded-sm border border-[#0b1f4d]/15 bg-white pl-11 pr-4 py-3 text-sm text-[#0b1f4d] placeholder:text-[#66718d] focus:border-[#f5b61c] focus-visible:ring-2 focus-visible:ring-[#f5b61c]/40 outline-none transition-colors"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <p
          data-testid="episode-count"
          className="font-mono text-xs uppercase tracking-widest text-[#66718d] mb-6"
        >
          {filtered.length} {filtered.length === 1 ? "episode" : "episodes"}
          {filtered.length > PER_PAGE && (
            <span className="text-[#7a8399]">
              {" "}
              · Page {current} of {totalPages}
            </span>
          )}
        </p>

        {filtered.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pageItems.map((ep) => (
                <EpisodeCard key={ep.id} ep={ep} />
              ))}
            </div>

            {totalPages > 1 && (
              <nav
                data-testid="episode-pagination"
                aria-label="Episode pagination"
                className="mt-12 flex items-center justify-center gap-2"
              >
                <button
                  data-testid="pagination-prev"
                  onClick={() => goTo(current - 1)}
                  disabled={current === 1}
                  aria-label="Previous page"
                  className="inline-flex items-center gap-1 rounded-sm border border-[#0b1f4d]/15 px-3 py-2 text-sm font-semibold text-[#374566] transition-colors hover:border-[#f5b61c] hover:text-[#0b1f4d] disabled:opacity-40 disabled:pointer-events-none"
                >
                  <ChevronLeft size={16} /> Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    data-testid={`pagination-page-${p}`}
                    onClick={() => goTo(p)}
                    aria-current={p === current ? "page" : undefined}
                    className={`h-9 w-9 rounded-sm text-sm font-semibold font-mono transition-colors ${
                      p === current
                        ? "bg-[#f5b61c] text-[#0a1630]"
                        : "border border-[#0b1f4d]/15 text-[#374566] hover:border-[#f5b61c] hover:text-[#0b1f4d]"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  data-testid="pagination-next"
                  onClick={() => goTo(current + 1)}
                  disabled={current === totalPages}
                  aria-label="Next page"
                  className="inline-flex items-center gap-1 rounded-sm border border-[#0b1f4d]/15 px-3 py-2 text-sm font-semibold text-[#374566] transition-colors hover:border-[#f5b61c] hover:text-[#0b1f4d] disabled:opacity-40 disabled:pointer-events-none"
                >
                  Next <ChevronRight size={16} />
                </button>
              </nav>
            )}
          </>
        ) : (
          <p className="text-[#4d5a78]" data-testid="episodes-empty">
            No episodes match “{q}”.
          </p>
        )}
      </section>
    </div>
  );
}
